import {PickupAppointmentType} from '../graphql/queries/getPickupAppointmentListing';
import {ZipcodeType} from '../graphql/queries/getLocationData';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
// Props for the drawer component
export interface PickupAppointmentDrawerProps {
    visible: boolean;
    onClose: () => void;
    appointmentId?: number;
    mode: 'create' | 'edit' | 'detail';
    onSuccess?: () => void;
    listPickupDate?: string; // 列表页面的筛选日期，用于新增时保持一致
    canCreateAppointment?: boolean; // 是否可以创建预约
    initialCooperationWarehouseId?: number; // 复揽跳转：创建模式下预填的揽收站点 ID
    initialChannelId?: number; // 复揽跳转：创建模式下预填的渠道 ID
    initialRepickupSourceId?: number; // 复揽跳转：原疑似虚假单 ID，建单时随 save 入参落库(repickupSourceAppointmentId)
    initialManualReason?: string; // 复揽跳转：预填的手动加单原因（由来源抽屉按站点当日有无单算好，见 SchedulePickupModal）
    // 地址审核池「确认预约单」复用本抽屉的只读确认态：改标题、隐藏常规操作、底部显示确认按钮
    reviewConfirmMode?: boolean;
    reviewConfirmLoading?: boolean;
    onReviewConfirm?: () => void | Promise<void>;
}

// Form data structure
export interface PickupAppointmentFormData {
    id?: number; // ID is always a number
    warehouseId: number;
    channelId: number;
    cooperationWarehouseId?: number;
    cooperationWarehouseName?: string;
    pickupSiteId?: number;
    appointmentNumber: string;

    // 联系信息
    contactName?: string;
    contactPhone?: string;
    countryCode: number;
    contactEmail?: string;
    ext?: string;

    // 地址信息
    addressLine1: string;
    addressLine2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipcode: string;
    timezone: string;

    // 工作时间配置
    workWeekdaysJson: number[];
    workStartTime: string;
    workEndTime: string;

    // 预约时间信息
    expectedPickupDate: string;
    expectedPickupTimeStart: string;
    expectedPickupTimeEnd: string;
    actualPickupDate?: string;
    actualPickupTimeStart?: string;
    actualPickupTimeEnd?: string;

    // 循环预约日期
    selectedDates: string[];

    // 包裹信息
    expectedParcelCount: number;
    /** 挂载运单数，与「预计揽收包裹」抽屉里的条数一致，不受手动编辑影响 */
    pickupOrderTotalCount?: number;
    actualParcelCount: number;
    expectedParcelUnit?: string;
    actualParcelUnit?: string;
    /** 车型：为空表示未单独指定，展示/筛选/导出时后端回退站点当日排期车型 */
    vehicleTypeId?: number;

    // 备注和追踪信息
    notes?: string;
    trackingNumbersJson?: string | string[];
    /** 上门扫描单号（只读展示，不随保存提交） */
    scanTrackingNumbers?: string[];
    pickupSerialNosJson?: string | string[];

    // 状态信息
    status: string;
    isFinished: boolean;
    isAbnormal: boolean;
    exceptionReasonCode?: string;
    photosS3KeysJson?: string[];
    photosS3UrlJson?: string[];
    exceptionPhotosS3KeysJson?: string[];
    exceptionPhotosS3UrlJson?: string[];
    cancelVoucherS3KeysJson?: string[];
    cancelVoucherS3UrlJson?: string[];
    /** 取消标识：MANUAL 人工 / BOT 机器人 / SYSTEM 系统；为空表示未记录（该字段上线前取消） */
    cancelType?: 'MANUAL' | 'BOT' | 'SYSTEM' | null;
    /** 取消原因：机器人取消为群消息上下文；系统 / 合并为原因码（详情不展示） */
    cancelReason?: string | null;
    /** BOL 提货单图片 URL，一单一张；未揽收或历史数据为空 */
    bolUrl?: string | null;
    cancelVoucherS3Keys?: string[];

    // 预约单来源（PickupAppointmentSourceEnum：STANDING_PICKUP/MANUAL/SYSTEM_SLA/SYSTEM/COPY）
    source?: string;

    // 手动加单原因（仅手动创建生效，默认 REGULAR_PICKUP）
    manualReason?: string;
}

// State interface for the hook
export interface PickupAppointmentDrawerState {
    loading: boolean;
    appointment: PickupAppointmentFormData | null;
    states: string[];
    cities: string[];
    zipcodes: string[];
    selectedState: string;
    selectedCity: string;
    selectedZipcode: string;
    statesLoading: boolean;
    citiesLoading: boolean;
    zipcodesLoading: boolean;
}


// 状态选项
export const statusOptions = [
    {value: 'CREATED', label: t('enum.pickupAppointmentStatus.created')},
    {value: 'DISPATCHING', label: t('enum.pickupAppointmentStatus.dispatching')},
    {value: 'DISPATCHED', label: t('enum.pickupAppointmentStatus.dispatched')},
    {value: 'PICKING_UP', label: t('enum.pickupAppointmentStatus.pickingUp')},
    {value: 'PICKED_UP', label: t('enum.pickupAppointmentStatus.pickedUp')},
    {value: 'PICKUP_FAILED', label: t('enum.pickupAppointmentStatus.pickupFailed')},
    {value: 'CANCELLED', label: t('enum.pickupAppointmentStatus.cancelled')}
];
    
// 包裹单位选项（用于过滤器，使用字符串值）
export const packageUnitFilterOptions = [
    {value: 'PIECE', label: t('enum.packageUnitFilterOptions.piece')},
    {value: 'BAG', label: t('enum.packageUnitFilterOptions.bag')},
    {value: 'CARTON', label: t('enum.packageUnitFilterOptions.carton')},
    {value: 'PALLET', label: t('enum.packageUnitFilterOptions.pallet')}
];

// 手动加单原因选项（值为后端 GraphQL 枚举名）
export const manualReasonOptions = [
    {value: 'REGULAR_PICKUP', label: t('enum.pickupManualReason.regularPickup')},
    {value: 'RE_PICKUP', label: t('enum.pickupManualReason.rePickup')},
    {value: 'PARTIAL_PICKUP', label: t('enum.pickupManualReason.partialPickup')},
    {value: 'VOLUME_OVERFLOW_SPLIT', label: t('enum.pickupManualReason.volumeOverflowSplit')}
];

// 默认加单原因
export const DEFAULT_MANUAL_REASON = 'REGULAR_PICKUP';

// 特殊加单原因（绕过单站点单日唯一性、排除合并）
export const SPECIAL_MANUAL_REASONS = ['RE_PICKUP', 'PARTIAL_PICKUP', 'VOLUME_OVERFLOW_SPLIT'];

// 星期选项 - 改为函数以支持国际化
export const getWeekdaysOptions = (t: (key: string) => string) => [
    {value: 1, label: t('enum.weekdaysOptions.monday')},
    {value: 2, label: t('enum.weekdaysOptions.tuesday')},
    {value: 3, label: t('enum.weekdaysOptions.wednesday')},
    {value: 4, label: t('enum.weekdaysOptions.thursday')},
    {value: 5, label: t('enum.weekdaysOptions.friday')},
    {value: 6, label: t('enum.weekdaysOptions.saturday')},
    {value: 7, label: t('enum.weekdaysOptions.sunday')}
];

// 时区选项
export const timezoneOptions = [
    {value: 'GMT-10', label: 'GMT-10'},
    {value: 'GMT-9', label: 'GMT-9'},
    {value: 'GMT-8', label: 'GMT-8'},
    {value: 'GMT-7', label: 'GMT-7'},
    {value: 'GMT-6', label: 'GMT-6'},
    {value: 'GMT-5', label: 'GMT-5'},
    {value: 'GMT-4', label: 'GMT-4'}
];

// 来源选项
export const sourceFilterOptions = [
    { label: t('enum.pickupAppointmentSource.standingPickup'), value: 'STANDING_PICKUP' },
    { label: t('enum.pickupAppointmentSource.manual'), value: 'MANUAL' },
    { label: t('enum.pickupAppointmentSource.systemSla'), value: 'SYSTEM_SLA' },
    { label: t('enum.pickupAppointmentSource.system'), value: 'SYSTEM' },
    { label: t('enum.pickupAppointmentSource.copy'), value: 'COPY' },
    { label: t('enum.pickupAppointmentSource.pickupBot'), value: 'PICKUP_BOT' },
];

